/**
 * PhaseApoderado — datos + documentos del Apoderado Legal (CONDICIONAL).
 *
 * Sólo se renderiza si ``data.has_apoderado === true``. Si está en false,
 * el orquestador en ``NewCustomerPhased.jsx`` salta esta phase directamente.
 *
 * Diferencias con el Representante:
 *  - representation_type = "apoderado" (clave para que el PDF y el
 *    screening la etiqueten distinto).
 *  - Doc obligatorio adicional: copia certificada del poder otorgado.
 *  - Doc adicional: constancia CURP o RFC / cédula de alta de obligaciones.
 *
 * Paridad con persona física: subir docs dispara OCR background y los
 * campos del form se pre-llenan en vivo. Aislamiento por sujeto vía
 * subject_draft_id="apo-main" (un único apoderado por draft).
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import SubjectFormFields from "./SubjectFormFields";
import SubjectDocUploader from "./SubjectDocUploader";
import DocFreshnessWarnings from "./DocFreshnessWarnings";
import useVigenciaRules from "./useVigenciaRules";
import useSubjectExtraction from "./useSubjectExtraction";
import mapExtractedToSubject from "./mapExtractedToSubject";
import PrefillPromptShell, { OcrUpsellHint } from "./PrefillPromptShell";
import { shellStateFromExtraction } from "./PhaseRepresentative";

const APO_DRAFT_ID = "apo-main";

export default function PhaseApoderado({
  data, ocrBySubject, onNext, onBack, loading, draftId,
}) {
  const apoFromData = (data.legal_representatives || []).find(
    (r) => r.representation_type === "apoderado",
  ) || {};
  const [profile, setProfile] = useState({
    representation_type: "apoderado",
    ...apoFromData,
  });
  const [warnKey, setWarnKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const { suffixFor, ocrEnabled } = useVigenciaRules();

  // Persisted OCR on phase re-entry (snapshot from update_phase).
  useEffect(() => {
    const ocr = (ocrBySubject || {}).apoderado?.[APO_DRAFT_ID];
    if (!ocr) return;
    setProfile((prev) => mergeFill(prev, ocr));
  }, [ocrBySubject]);

  // Live OCR polling subject-scoped.
  const extraction = useSubjectExtraction({
    draftId,
    subjectType: "apoderado",
    subjectDraftId: APO_DRAFT_ID,
    enabled: true,
    ocrEnabled,
    refreshKey,
  });
  useEffect(() => {
    if (!extraction.allDone) return;
    const mapped = mapExtractedToSubject(extraction.mergedData);
    if (Object.keys(mapped).length === 0) return;
    setProfile((prev) => mergeFill(prev, mapped));
  }, [extraction.allDone, extraction.mergedData]);

  const valid = (profile.full_name || "").trim();
  const continueBlocked = !valid || loading || extraction.isExtracting;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Apoderado Legal</h2>
        <p className="text-sm text-slate-400">
          {ocrEnabled
            ? "Sube primero los documentos del apoderado; sus datos se autocompletarán."
            : "Captura los datos del apoderado y sus documentos."}
        </p>
      </div>

      {/* PRIMARY: prominent prefill card (Pro). Manual path is the form
          below; no return-banner here because the uploader stays
          visible inside the shell, so re-engaging OCR is just "upload
          a document". */}
      {ocrEnabled && (
        <PrefillPromptShell
          testIdPrefix="apo-prefill"
          state={shellStateFromExtraction(extraction)}
          elapsedSec={extraction.elapsedSec}
          title="Sube los documentos del apoderado y autocompletamos sus datos"
          subtitle="Identificación oficial, comprobante de domicilio y copia certificada del poder."
          collapseOnDone={false}
        >
          <SubjectDocUploader
            draftId={draftId}
            subjectType="apoderado"
            subjectDraftId={APO_DRAFT_ID}
            onUploaded={() => { setWarnKey((k) => k + 1); setRefreshKey((k) => k + 1); }}
            docs={[
              { docType: "apo_identificacion",         label: "Identificación oficial",         required: true },
              { docType: "apo_comprobante_domicilio",  label: `Comprobante de domicilio${suffixFor("apo_comprobante_domicilio")}`, required: true },
              { docType: "apo_copia_certificada_poder", label: "Copia certificada del poder",     required: true },
              { docType: "apo_constancia_curp_o_rfc",  label: "Constancia CURP / RFC / Cédula de obligaciones", required: false },
            ]}
          />
        </PrefillPromptShell>
      )}
      {!ocrEnabled && (
        <>
          <OcrUpsellHint
            testId="apo-ocr-disabled-hint"
            text="El pre-llenado automático desde los documentos del apoderado está disponible en"
          />
          <SubjectDocUploader
            draftId={draftId}
            subjectType="apoderado"
            subjectDraftId={APO_DRAFT_ID}
            onUploaded={() => { setWarnKey((k) => k + 1); setRefreshKey((k) => k + 1); }}
            docs={[
              { docType: "apo_identificacion",         label: "Identificación oficial",         required: true },
              { docType: "apo_comprobante_domicilio",  label: `Comprobante de domicilio${suffixFor("apo_comprobante_domicilio")}`, required: true },
              { docType: "apo_copia_certificada_poder", label: "Copia certificada del poder",     required: true },
              { docType: "apo_constancia_curp_o_rfc",  label: "Constancia CURP / RFC / Cédula de obligaciones", required: false },
            ]}
          />
        </>
      )}
      <DocFreshnessWarnings
        draftId={draftId}
        subjectType="apoderado"
        subjectId={APO_DRAFT_ID}
        refreshKey={warnKey}
        testIdSuffix="apoderado"
      />

      {/* Form abajo */}
      <SubjectFormFields
        profile={profile}
        onChange={setProfile}
        subjectKey="apo"
        includeContact
        includeResidence
        includePEP
        includeIdExpiry
      />

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={loading}>← Atrás</Button>
        <Button data-testid="apo-continue-btn" disabled={continueBlocked}
                onClick={() => onNext({
                  legal_representatives: [
                    ...(data.legal_representatives || []).filter(
                      (r) => r.representation_type !== "apoderado",
                    ),
                    { ...profile, _draft_subject_id: APO_DRAFT_ID },
                  ],
                })}>
          {extraction.isExtracting
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Esperando OCR…</>
            : loading ? "Guardando…" : "Continuar →"}
        </Button>
      </div>
    </div>
  );
}

function mergeFill(prev, fields) {
  const next = { ...prev };
  for (const [k, v] of Object.entries(fields)) {
    if (!v) continue;
    if (prev[k] === undefined || prev[k] === "" || prev[k] === null) {
      next[k] = v;
    }
  }
  return next;
}
